import { useCallback, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { SourceAndLogData } from 'common/types';
import {
  getLogs,
  getParamsFromValue,
  getSources,
  getValueFromParams,
} from 'common/util/sourcesAndLogs';
import { useSetSearchParams } from 'hooks/useSetSearchParams';

import ComboBox from 'components/common/ComboBox/ComboBox';
import { draftSearchParamState } from 'components/common/Managers/RouteStateManager.state';
import './SearchFields.scss';

type SearchFieldsProps = {
  sourcesAndLogs: SourceAndLogData | null;
};

/**
 * This component allows the user to set search fields via a combo box (input plus a dropdown list).
 * The two search fields are for sources (server hosts and Kubernetes clusters) and logs (logfiles and
 * Kubernetes controllers). The list of logs that can be shown in the lower dropdown is constrained
 * to those belonging to the filtered list of sources.
 *
 * @prop sourcesAndLogs    source and log data, parsed for fast filtering
 */
function SearchFields({
  sourcesAndLogs,
}: SearchFieldsProps): JSX.Element {

  const [ sourceList, setSourceList ] = useState(K.EMPTY_ARRAY as Array<string>);
  const [ logList, setLogList ] = useState(K.EMPTY_ARRAY as Array<string>);
  const setSearchParams = useSetSearchParams();
  const [ draftParams, setDraftParams ] = useRecoilState(draftSearchParamState);

  // create the initial master lists
  useEffect(() => {
    if (sourcesAndLogs) {
      setSourceList(getSources(sourcesAndLogs));
      setLogList(getLogs(sourcesAndLogs, []));
    }
  }, [ sourcesAndLogs ]);

  // list of logs needs to be updated when filtered list of sources changes
  const onSourceListChange = useCallback((list) => {
    if (sourcesAndLogs) {
      setLogList(getLogs(sourcesAndLogs, list));
    }
  }, [ sourcesAndLogs, setLogList ]);

  // values of input track draft state
  const sourceValue = getValueFromParams(draftParams, true);
  const logValue = getValueFromParams(draftParams, false);

  const onSourceChange = (value: string): void => {
    const urlParams = getParamsFromValue(sourcesAndLogs, value, true, sourceValue, logValue);
    setDraftParams((state) => ({ ...state, ...urlParams }));
  };

  const onLogChange = (value: string): void => {
    const urlParams = getParamsFromValue(sourcesAndLogs, value, false, sourceValue, logValue);
    setDraftParams((state) => ({ ...state, ...urlParams }));
  };

  // user has clicked an item in the sources or logs list
  const onSelect = (item: string, isSource: boolean): void => {
    setSearchParams(getParamsFromValue(sourcesAndLogs, item, isSource, sourceValue, logValue));
  };

  // user has typed text into the source or log input and pressed Enter
  // we assume it's a host or a logfile (and not Kubernetes-related) since we have no way to know
  const onEnter = (item: string, isSource: boolean): void => {
    // normalize value to be text surrounded by * for "contains" comparison (if user has not provided wildcards)
    let value = item;
    if (!value.includes('*')) {
      value = value ? '*' + value + '*' : '';
    }
    setSearchParams(getParamsFromValue(sourcesAndLogs, value, isSource, sourceValue, logValue), true);
  };

  // user has cleared the input; run search
  const onClear = (isSource: boolean): void => {
    setSearchParams(getParamsFromValue(sourcesAndLogs, '', isSource, sourceValue, logValue));
  };

  return (
    <div className="SearchFields">
      <ComboBox
        className="SearchFields__Input"
        placeholder="Source (*=wildcard)"
        size="sm"
        value={sourceValue}
        items={sourceList}
        limit={K.MAX_SEARCH_FIELD_ITEMS}
        onChange={onSourceChange}
        onSelect={(value: string): void => onSelect(value, true)}
        onEnter={(value: string): void => onEnter(value, true)}
        onClear={(): void => onClear(true)}
        onListChange={onSourceListChange}
        testId={ELEMENT.searchSidebarSource}
        selectOnEnter
      />
      <ComboBox
        className="SearchFields__Input"
        placeholder="Logfile (*=wildcard)"
        size="sm"
        value={logValue}
        items={logList}
        limit={K.MAX_SEARCH_FIELD_ITEMS}
        onChange={onLogChange}
        onSelect={(value: string): void => onSelect(value, false)}
        onEnter={(value: string): void => onEnter(value, false)}
        onClear={(): void => onClear(false)}
        testId={ELEMENT.searchSidebarLogfile}
        selectOnEnter
      />
    </div>
  );
}

export default SearchFields;
